import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Países disponíveis para marketing internacional
const INTERNATIONAL_COUNTRIES = [
  { code: 'BR', name: 'Brasil', flag: '🇧🇷', language: 'pt' },
  { code: 'US', name: 'Estados Unidos', flag: '🇺🇸', language: 'en' },
  { code: 'CA', name: 'Canadá', flag: '🇨🇦', language: 'en' },
  { code: 'GB', name: 'Reino Unido', flag: '🇬🇧', language: 'en' },
  { code: 'AU', name: 'Austrália', flag: '🇦🇺', language: 'en' },
  { code: 'DE', name: 'Alemanha', flag: '🇩🇪', language: 'de' },
  { code: 'MX', name: 'México', flag: '🇲🇽', language: 'es' },
  { code: 'ES', name: 'Espanha', flag: '🇪🇸', language: 'es' }
];

const SocialAutomation = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [internationalCampaigns, setInternationalCampaigns] = useState([]);
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  const [showInternationalMode, setShowInternationalMode] = useState(false);
  const [bestTimes, setBestTimes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCustomTimes, setShowCustomTimes] = useState(false);
  const [automationStatus, setAutomationStatus] = useState(null);
  const [userLimits, setUserLimits] = useState(null);

  const [campaignForm, setCampaignForm] = useState({
    product_name: '',
    product_description: '',
    affiliate_link: '',
    platforms: [],
    product_type: 'digital',
    audience: 'geral',
    frequency: 'balanced',
    duration_days: 30,
    custom_times: {},
    // Novos campos para marketing internacional
    target_countries: ['BR'],
    auto_translate: true
  });

  useEffect(() => {
    fetchAutomationData();
    fetchBestTimes();
    fetchAutomationStatus();
    fetchUserLimits();
    fetchInternationalCampaigns();
    
    // Atualizar status a cada 30 segundos
    const interval = setInterval(fetchAutomationStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchUserLimits = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const response = await axios.get(`${API}/user/limits`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUserLimits(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch user limits:', error);
    }
  };

  const fetchInternationalCampaigns = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const response = await axios.get(`${API}/international/campaigns`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setInternationalCampaigns(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch international campaigns:', error);
    }
  };

  const fetchAutomationStatus = async () => {
    try {
      const response = await axios.get(`${API}/social/automation-status`);
      setAutomationStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch automation status:', error);
    }
  };

  const fetchAutomationData = async () => {
    try {
      const [campaignsRes, postsRes] = await Promise.all([
        axios.get(`${API}/social/campaigns`),
        axios.get(`${API}/social/scheduled-posts`)
      ]);
      
      setCampaigns(campaignsRes.data);
      setScheduledPosts(postsRes.data);
    } catch (error) {
      console.error('Failed to fetch automation data:', error);
      setCampaigns([]);
      setScheduledPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchBestTimes = async (platform = 'instagram', productType = 'digital') => {
    try {
      const response = await axios.get(`${API}/social/best-times?platform=${platform}&product_type=${productType}`);
      setBestTimes(response.data);
    } catch (error) {
      console.error('Failed to fetch best times:', error);
    }
  };

  const createCampaign = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Se tem mais de 1 país selecionado, criar campanha internacional
      if (campaignForm.target_countries.length > 1 || 
          (campaignForm.target_countries.length === 1 && campaignForm.target_countries[0] !== 'BR')) {
        
        // Criar campanha internacional
        const internationalData = {
          product_name: campaignForm.product_name,
          product_description: campaignForm.product_description,
          affiliate_link: campaignForm.affiliate_link,
          target_regions: campaignForm.target_countries,
          platforms: campaignForm.platforms,
          campaign_duration: campaignForm.duration_days,
          auto_translate: campaignForm.auto_translate,
          schedule_by_timezone: true
        };

        const response = await axios.post(`${API}/international/create-campaign`, internationalData, {
          headers: { Authorization: `Bearer ${token}` }
        });

        alert(`✅ Campanha INTERNACIONAL criada!\n\nPaíses: ${campaignForm.target_countries.join(', ')}\n\n${response.data.explanation?.how_it_works || 'Posts serão publicados automaticamente'}`);
        
      } else {
        // Criar campanha nacional normal
        const response = await axios.post(`${API}/social/create-campaign`, campaignForm);
        alert(`✅ Campanha criada com sucesso!\n\nHorários programados:\n${JSON.stringify(response.data.posting_schedule, null, 2)}`);
      }
      
      setShowCreateCampaign(false);
      resetForm();
      fetchAutomationData();
      fetchInternationalCampaigns();
    } catch (error) {
      console.error('Failed to create campaign:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao criar campanha. Verifique os dados e tente novamente.';
      alert(errorMsg);
    }
  };

  const resetForm = () => {
    setCampaignForm({
      product_name: '',
      product_description: '',
      affiliate_link: '',
      platforms: [],
      product_type: 'digital',
      audience: 'geral',
      frequency: 'balanced',
      duration_days: 30,
      custom_times: {},
      target_countries: ['BR'],
      auto_translate: true
    });
    setShowInternationalMode(false);
  };

  const deleteCampaign = async (campaignId, isInternational = false) => {
    if (!window.confirm('Tem certeza que deseja excluir esta campanha?')) return;
    
    try {
      const token = localStorage.getItem('token');
      
      if (isInternational) {
        await axios.delete(`${API}/international/campaigns/${campaignId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.delete(`${API}/social/campaigns/${campaignId}`);
      }
      
      fetchAutomationData();
      fetchInternationalCampaigns();
    } catch (error) {
      console.error('Failed to delete campaign:', error);
      alert('Erro ao excluir campanha.');
    }
  };

  const handlePlatformToggle = (platform) => {
    const updatedPlatforms = campaignForm.platforms.includes(platform)
      ? campaignForm.platforms.filter(p => p !== platform)
      : [...campaignForm.platforms, platform];
    
    setCampaignForm({ ...campaignForm, platforms: updatedPlatforms });
  };

  const handleCountryToggle = (countryCode) => {
    const isLimited = userLimits && userLimits.allowed_countries !== 'all' && 
                      !userLimits.allowed_countries?.includes(countryCode.toLowerCase());
    
    if (isLimited && !campaignForm.target_countries.includes(countryCode)) {
      alert('🔒 Upgrade necessário!\n\nSeu plano atual só permite marketing no Brasil.\nFaça upgrade para desbloquear países internacionais!');
      return;
    }

    const updatedCountries = campaignForm.target_countries.includes(countryCode)
      ? campaignForm.target_countries.filter(c => c !== countryCode)
      : [...campaignForm.target_countries, countryCode];
    
    // Manter pelo menos 1 país selecionado
    if (updatedCountries.length === 0) {
      return;
    }
    
    setCampaignForm({ ...campaignForm, target_countries: updatedCountries });
  };

  const handleCustomTimeChange = (platform, times) => {
    setCampaignForm({
      ...campaignForm,
      custom_times: {
        ...campaignForm.custom_times,
        [platform]: times
      }
    });
  };

  // Verificar se país está disponível no plano do usuário
  const isCountryAvailable = (countryCode) => {
    if (!userLimits) return true;
    if (userLimits.allowed_countries === 'all') return true;
    if (userLimits.is_admin) return true;
    return userLimits.allowed_countries?.includes(countryCode.toLowerCase()) || countryCode === 'BR';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">⚡ Automação de Redes Sociais</h1>
        <p className="text-gray-600">Configure postagens automáticas para suas campanhas de afiliados</p>
      </div>

      {/* Status do Sistema de Automação */}
      {automationStatus && (
        <div className={`mb-6 p-4 rounded-lg border-2 ${
          automationStatus.worker_active 
            ? 'bg-green-50 border-green-500' 
            : 'bg-red-50 border-red-500'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`w-4 h-4 rounded-full animate-pulse ${
                automationStatus.worker_active ? 'bg-green-500' : 'bg-red-500'
              }`}></div>
              <div>
                <h3 className={`font-bold ${
                  automationStatus.worker_active ? 'text-green-800' : 'text-red-800'
                }`}>
                  {automationStatus.worker_active 
                    ? '🟢 Sistema ATIVO - Rodando 24/7' 
                    : '🔴 Sistema INATIVO'}
                </h3>
                <p className="text-sm text-gray-600">
                  {automationStatus.message}
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900">{automationStatus.posts_today}</div>
              <div className="text-xs text-gray-500">Posts hoje</div>
            </div>
          </div>
          
          {automationStatus.worker_active && (
            <div className="mt-3 pt-3 border-t border-green-200">
              <p className="text-sm text-green-700">
                ✅ <strong>Você pode fechar o navegador!</strong> O sistema continua postando automaticamente no servidor.
                <br />
                📊 Verificação a cada {automationStatus.check_interval} | 
                🎯 {automationStatus.active_campaigns} campanha(s) ativa(s)
              </p>
            </div>
          )}
        </div>
      )}

      {/* Banner Marketing Internacional */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-500 text-white p-6 rounded-lg shadow mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold mb-2">🌍 Marketing Internacional</h2>
            <p className="text-sm opacity-90">
              Alcance clientes nos EUA, Canadá, Reino Unido e mais! Posts traduzidos e publicados no horário ideal de cada país.
            </p>
          </div>
          <div className="text-4xl">🚀</div>
        </div>
      </div>

      {/* Guia de Melhores Horários */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-6 rounded-lg shadow mb-8">
        <h2 className="text-xl font-bold mb-4">📊 Melhores Horários para Vendas</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-semibold mb-2">📱 Produtos DIGITAIS (Cursos, E-books)</h3>
            <ul className="space-y-1 text-sm">
              <li>• <strong>Melhor horário:</strong> 18h às 22h</li>
              <li>• <strong>Melhores dias:</strong> Terça, Quarta, Quinta</li>
              <li>• <strong>Por quê:</strong> Pessoas têm tempo livre após o trabalho</li>
              <li>• <strong>Posts/dia:</strong> 2-4 (variando conteúdo)</li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold mb-2">📦 Produtos FÍSICOS (Eletrônicos, Moda)</h3>
            <ul className="space-y-1 text-sm">
              <li>• <strong>Melhor horário:</strong> 10h às 14h e 19h às 21h</li>
              <li>• <strong>Melhores dias:</strong> Segunda a Quinta</li>
              <li>• <strong>Por quê:</strong> Pesquisa durante pausas e à noite</li>
              <li>• <strong>Posts/dia:</strong> 1-2 (foco em qualidade)</li>
            </ul>
          </div>
        </div>
        <div className="mt-4 bg-white/20 rounded p-3">
          <p className="text-sm">
            💡 <strong>Dica:</strong> Evite postar nos finais de semana - engajamento é 30% menor!
          </p>
        </div>
      </div>

      {/* Estatísticas das Campanhas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-3xl font-bold text-blue-600">{campaigns.length + internationalCampaigns.length}</div>
          <div className="text-gray-600">Campanhas Ativas</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-3xl font-bold text-green-600">{scheduledPosts.length}</div>
          <div className="text-gray-600">Posts Agendados</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-3xl font-bold text-purple-600">
            {campaigns.reduce((acc, c) => acc + (c.posts_created || 0), 0)}
          </div>
          <div className="text-gray-600">Posts Publicados</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-3xl font-bold text-orange-600">
            {internationalCampaigns.length}
          </div>
          <div className="text-gray-600">🌍 Internacionais</div>
        </div>
      </div>

      {/* Botão Criar Campanha */}
      <div className="mb-8">
        <button
          onClick={() => setShowCreateCampaign(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg shadow-lg"
        >
          🚀 Criar Nova Campanha Automatizada
        </button>
      </div>

      {/* Modal de Criação de Campanha */}
      {showCreateCampaign && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">🚀 Criar Campanha Automatizada</h3>
              <button onClick={() => { setShowCreateCampaign(false); resetForm(); }} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
            </div>
            
            <div className="space-y-4 max-h-[70vh] overflow-y-auto">
              {/* Informações do Produto */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Produto *</label>
                  <input
                    type="text"
                    value={campaignForm.product_name}
                    onChange={(e) => setCampaignForm({...campaignForm, product_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: Curso de Marketing Digital"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Link de Afiliado *</label>
                  <input
                    type="url"
                    value={campaignForm.affiliate_link}
                    onChange={(e) => setCampaignForm({...campaignForm, affiliate_link: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    placeholder="https://..."
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrição do Produto</label>
                <textarea
                  rows={2}
                  value={campaignForm.product_description}
                  onChange={(e) => setCampaignForm({...campaignForm, product_description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder="Descreva os principais benefícios..."
                />
              </div>

              {/* NOVO: Seleção de Países */}
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-lg border-2 border-purple-200">
                <div className="flex justify-between items-center mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    🌍 Países para Marketing *
                  </label>
                  <button
                    onClick={() => setShowInternationalMode(!showInternationalMode)}
                    className={`text-sm px-3 py-1 rounded-full ${
                      showInternationalMode 
                        ? 'bg-purple-600 text-white' 
                        : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    {showInternationalMode ? '🌍 Internacional' : '🇧🇷 Apenas Brasil'}
                  </button>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {INTERNATIONAL_COUNTRIES.map(country => {
                    const isAvailable = isCountryAvailable(country.code);
                    const isSelected = campaignForm.target_countries.includes(country.code);
                    
                    return (
                      <button
                        key={country.code}
                        onClick={() => handleCountryToggle(country.code)}
                        disabled={!showInternationalMode && country.code !== 'BR'}
                        className={`p-2 rounded-lg border-2 text-center transition-all ${
                          isSelected
                            ? 'border-purple-500 bg-purple-100'
                            : isAvailable
                              ? 'border-gray-200 hover:border-purple-300'
                              : 'border-gray-100 bg-gray-50 opacity-50'
                        } ${!showInternationalMode && country.code !== 'BR' ? 'opacity-40 cursor-not-allowed' : ''}`}
                      >
                        <div className="text-2xl">{country.flag}</div>
                        <div className="text-xs font-medium">{country.name}</div>
                        {!isAvailable && <div className="text-xs text-orange-500">🔒</div>}
                      </button>
                    );
                  })}
                </div>

                {showInternationalMode && (
                  <div className="mt-3 bg-purple-100 rounded p-2 text-sm text-purple-800">
                    <strong>✨ Modo Internacional Ativo:</strong> Posts serão traduzidos automaticamente e publicados no melhor horário local de cada país selecionado.
                  </div>
                )}

                {/* Opção de tradução automática */}
                {showInternationalMode && campaignForm.target_countries.length > 1 && (
                  <div className="mt-3 flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="autoTranslate"
                      checked={campaignForm.auto_translate}
                      onChange={(e) => setCampaignForm({...campaignForm, auto_translate: e.target.checked})}
                      className="h-4 w-4 text-purple-600"
                    />
                    <label htmlFor="autoTranslate" className="text-sm text-gray-700">
                      🔄 Traduzir conteúdo automaticamente para cada idioma
                    </label>
                  </div>
                )}
              </div>

              {/* Tipo de Produto */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Produto (afeta horários recomendados)</label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: 'digital', label: '📱 Digital', desc: 'Cursos, e-books, software' },
                    { value: 'physical', label: '📦 Físico', desc: 'Eletrônicos, moda, casa' },
                    { value: 'general', label: '🌐 Geral', desc: 'Misto ou serviços' }
                  ].map(type => (
                    <button
                      key={type.value}
                      onClick={() => {
                        setCampaignForm({...campaignForm, product_type: type.value});
                        fetchBestTimes(campaignForm.platforms[0] || 'instagram', type.value);
                      }}
                      className={`p-3 rounded-lg border-2 text-left ${
                        campaignForm.product_type === type.value
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-medium">{type.label}</div>
                      <div className="text-xs text-gray-500">{type.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Plataformas */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Plataformas para Automação *</label>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                  {[
                    { key: 'instagram', icon: '📸', name: 'Instagram' },
                    { key: 'facebook', icon: '👥', name: 'Facebook' },
                    { key: 'tiktok', icon: '🎵', name: 'TikTok' },
                    { key: 'youtube', icon: '📺', name: 'YouTube' },
                    { key: 'twitter', icon: '🐦', name: 'Twitter' },
                    { key: 'linkedin', icon: '💼', name: 'LinkedIn' }
                  ].map(platform => (
                    <button
                      key={platform.key}
                      onClick={() => handlePlatformToggle(platform.key)}
                      className={`p-2 rounded-lg border-2 text-center ${
                        campaignForm.platforms.includes(platform.key)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-xl">{platform.icon}</div>
                      <div className="text-xs">{platform.name}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Frequência de Postagem */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Frequência de Postagem</label>
                  <select
                    value={campaignForm.frequency}
                    onChange={(e) => setCampaignForm({...campaignForm, frequency: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="conservative">🐢 Conservador (1 post/dia)</option>
                    <option value="balanced">⚖️ Equilibrado (2-3 posts/dia)</option>
                    <option value="aggressive">🚀 Agressivo (3-5 posts/dia)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duração (dias)</label>
                  <input
                    type="number"
                    value={campaignForm.duration_days}
                    onChange={(e) => setCampaignForm({...campaignForm, duration_days: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    min={1}
                    max={365}
                  />
                </div>
              </div>

              {/* Horários Personalizados */}
              <div className="border rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <label className="text-sm font-medium text-gray-700">Horários de Postagem</label>
                  <button
                    onClick={() => setShowCustomTimes(!showCustomTimes)}
                    className="text-sm text-blue-600 hover:underline"
                  >
                    {showCustomTimes ? '🤖 Usar Automático' : '⏰ Definir Manualmente'}
                  </button>
                </div>
                
                {showCustomTimes ? (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-500 mb-2">
                      Defina horários personalizados para cada plataforma (formato 24h, separados por vírgula):
                    </p>
                    {campaignForm.platforms.map(platform => (
                      <div key={platform} className="flex items-center gap-2">
                        <span className="w-24 text-sm font-medium capitalize">{platform}:</span>
                        <input
                          type="text"
                          placeholder="09:00, 12:00, 18:00"
                          onChange={(e) => handleCustomTimeChange(platform, e.target.value.split(',').map(t => t.trim()))}
                          className="flex-1 px-3 py-1 border border-gray-300 rounded text-sm"
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-green-50 p-3 rounded">
                    <p className="text-sm text-green-800">
                      ✅ <strong>Modo Automático:</strong> Os horários serão definidos automaticamente com base no tipo de produto ({campaignForm.product_type}) e nas melhores práticas de cada plataforma.
                      {showInternationalMode && campaignForm.target_countries.length > 1 && (
                        <><br /><br />🌍 <strong>Internacional:</strong> Posts serão publicados no melhor horário LOCAL de cada país selecionado.</>
                      )}
                    </p>
                  </div>
                )}
              </div>

              {/* Resumo */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">📋 Resumo da Campanha</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• <strong>Produto:</strong> {campaignForm.product_name || 'Não definido'}</li>
                  <li>• <strong>Tipo:</strong> {campaignForm.product_type}</li>
                  <li>• <strong>Plataformas:</strong> {campaignForm.platforms.join(', ') || 'Nenhuma selecionada'}</li>
                  <li>• <strong>Frequência:</strong> {campaignForm.frequency}</li>
                  <li>• <strong>Duração:</strong> {campaignForm.duration_days} dias</li>
                  <li>• <strong>🌍 Países:</strong> {campaignForm.target_countries.map(c => 
                    INTERNATIONAL_COUNTRIES.find(ic => ic.code === c)?.name || c
                  ).join(', ')}</li>
                  {showInternationalMode && <li>• <strong>Tradução:</strong> {campaignForm.auto_translate ? 'Automática' : 'Manual'}</li>}
                </ul>
              </div>

              {/* Botões */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={createCampaign}
                  disabled={!campaignForm.product_name || !campaignForm.affiliate_link || campaignForm.platforms.length === 0}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-bold py-2 px-4 rounded-lg"
                >
                  🚀 Criar Campanha {showInternationalMode && campaignForm.target_countries.length > 1 ? 'Internacional' : ''}
                </button>
                <button
                  onClick={() => { setShowCreateCampaign(false); resetForm(); }}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Campanhas Internacionais */}
      {internationalCampaigns.length > 0 && (
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="p-4 border-b bg-gradient-to-r from-purple-50 to-blue-50">
            <h2 className="text-xl font-semibold text-gray-900">🌍 Campanhas Internacionais</h2>
          </div>
          
          <div className="divide-y">
            {internationalCampaigns.map(campaign => (
              <div key={campaign.id} className="p-4 hover:bg-gray-50">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold text-gray-900">{campaign.product_name}</h3>
                    <p className="text-sm text-gray-500">
                      Países: {campaign.target_regions?.map(r => 
                        INTERNATIONAL_COUNTRIES.find(c => c.code === r)?.flag || r
                      ).join(' ')} | 
                      Plataformas: {campaign.platforms?.join(', ')}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        campaign.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {campaign.status === 'active' ? '🟢 Ativa' : '⏸️ Pausada'}
                      </span>
                      {campaign.auto_translate && (
                        <span className="px-2 py-1 rounded text-xs bg-purple-100 text-purple-800">
                          🔄 Tradução Auto
                        </span>
                      )}
                      <span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-800">
                        🌍 {campaign.target_regions?.length || 0} países
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => deleteCampaign(campaign.id, true)}
                    className="text-red-600 hover:text-red-800"
                  >
                    🗑️ Excluir
                  </button>
                </div>
                
                {/* Posts por região */}
                {campaign.posts_by_region && Object.keys(campaign.posts_by_region).length > 0 && (
                  <div className="mt-3 bg-gray-50 p-3 rounded text-sm">
                    <strong>📊 Posts por país:</strong>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {Object.entries(campaign.posts_by_region).map(([region, count]) => (
                        <span key={region} className="px-2 py-1 bg-white rounded border">
                          {INTERNATIONAL_COUNTRIES.find(c => c.code === region)?.flag || region}: {count}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lista de Campanhas Nacionais */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <h2 className="text-xl font-semibold text-gray-900">📋 Suas Campanhas</h2>
        </div>
        
        {campaigns.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <div className="text-4xl mb-2">📭</div>
            <p>Nenhuma campanha criada ainda.</p>
            <p className="text-sm">Clique em "Criar Nova Campanha" para começar!</p>
          </div>
        ) : (
          <div className="divide-y">
            {campaigns.filter(c => !c.international_parent).map(campaign => (
              <div key={campaign.id} className="p-4 hover:bg-gray-50">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold text-gray-900">{campaign.product_name}</h3>
                    <p className="text-sm text-gray-500">
                      Plataformas: {campaign.platforms?.join(', ')} | 
                      Tipo: {campaign.product_type} |
                      Frequência: {campaign.frequency}
                    </p>
                    <div className="mt-2 flex gap-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        campaign.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {campaign.status === 'active' ? '🟢 Ativa' : '⏸️ Pausada'}
                      </span>
                      <span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-800">
                        {campaign.posts_created || 0} posts publicados
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => deleteCampaign(campaign.id, false)}
                    className="text-red-600 hover:text-red-800"
                  >
                    🗑️ Excluir
                  </button>
                </div>
                
                {/* Horários da Campanha */}
                {campaign.posting_schedule && (
                  <div className="mt-3 bg-gray-50 p-3 rounded text-sm">
                    <strong>⏰ Horários programados:</strong>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                      {Object.entries(campaign.posting_schedule).map(([platform, schedule]) => (
                        <div key={platform} className="flex items-center gap-1">
                          <span className="font-medium capitalize">{platform}:</span>
                          <span className="text-gray-600">
                            {schedule.times?.join(', ') || 'Automático'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Dicas de Otimização */}
      <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-yellow-800 mb-4">💡 Dicas para Maximizar Vendas</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-yellow-700">
          <div>
            <h4 className="font-medium mb-2">Para Produtos Digitais:</h4>
            <ul className="space-y-1">
              <li>• Poste entre 18h-22h quando pessoas têm tempo livre</li>
              <li>• Use gatilhos de urgência ("Última chance!")</li>
              <li>• Compartilhe depoimentos e resultados</li>
              <li>• TikTok e Instagram têm maior conversão</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium mb-2">Para Marketing Internacional:</h4>
            <ul className="space-y-1">
              <li>• 🇺🇸 EUA: Melhor horário 9h-12h e 17h-20h (EST)</li>
              <li>• 🇬🇧 UK: Foco em qualidade, tom profissional</li>
              <li>• 🇩🇪 Alemanha: Textos precisos e detalhados</li>
              <li>• 🇲🇽 México: Tom amigável e familiar</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocialAutomation;
